import React, { useCallback } from "react";
import { ConnectorCardHeader } from "../molecules/ConnectorCardHeader";
import { ConnectorActionButton } from "../atoms/ConnectorActionButton";
import { TagChip } from "../atoms/TagChip";
import { CardButton, Body, Description, Footer } from "./ConnectorCard.styles";
import { getConnectorAction, getConnectorCardAriaLabel } from "./ConnectorCard.utils";
import type { ConnectorCardProps } from "./ConnectorCard.types";

/**
 * Marketplace connector card — accepts a registry row directly so the same
 * component can be reused by the Connections list (UI-2).
 *
 * Whole card is the click target (a single <button>); the footer affordance is
 * a visual-only <span> rendered via ConnectorActionButton to avoid nested
 * interactive elements.
 *
 * A `comingSoon` card is disabled: it keeps the identity and description but
 * swaps the connect action for a tag and cannot be activated.
 */
export const ConnectorCard: React.FC<ConnectorCardProps> = ({
  catalogKey,
  displayName,
  iconUrl,
  providerLabel,
  description,
  status,
  connectionId,
  instanceCount,
  comingSoon = false,
  comingSoonLabel,
  onClick,
  className,
}) => {
  const action = getConnectorAction(status, instanceCount, connectionId);

  const handleClick = useCallback(() => {
    onClick(catalogKey);
  }, [onClick, catalogKey]);

  return (
    <CardButton
      type="button"
      className={className}
      onClick={handleClick}
      disabled={comingSoon}
      aria-label={getConnectorCardAriaLabel(
        displayName,
        comingSoon ? { comingSoonLabel: comingSoonLabel ?? "" } : undefined,
      )}
      data-testid="connector-card"
    >
      <Body>
        <ConnectorCardHeader
          displayName={displayName}
          iconUrl={iconUrl}
          providerLabel={providerLabel}
          status={status}
        />
        <Description>{description}</Description>
      </Body>
      <Footer>
        {comingSoon ? (
          comingSoonLabel ? <TagChip label={comingSoonLabel} /> : null
        ) : (
          <ConnectorActionButton
            label={action.label}
            withChevron={action.withChevron}
          />
        )}
      </Footer>
    </CardButton>
  );
};

export default ConnectorCard;
